import { useEffect, useMemo, useState } from "react";
import { FileDown, Pencil, Play as PlayIcon, RotateCcw, Target } from "lucide-react";
import { toast } from "sonner";
import { classifyHit, resolvePlayers } from "@/lib/geometry";
import { formatApiError } from "@/lib/api";
import { PlayField } from "@/components/field/PlayField";
import { SituationControls } from "@/components/controls/SituationControls";
import { Legend } from "@/components/Legend";
import { ExplanationCard, PlayBadge } from "@/components/ExplanationCard";
import { EditPanel } from "@/components/edit/EditPanel";
import { usePlayDraft } from "@/hooks/usePlayDraft";
import { useReplay } from "@/hooks/useReplay";

export const readyPositions = (cfg) =>
  Object.fromEntries(Object.entries(cfg.ready).map(([pos, pt]) => [pos, { pos, pt, job: "hold", label: "Ready" }]));

export const PlaybookView = ({ cfg, loadPlays, editable, onSave, onReset, jump, onExportPdf, onAssign }) => {
  const [sit, setSit] = useState({ outs: 0, runners: 0, hitType: "ground" });
  const [plays, setPlays] = useState([]);
  const [hit, setHit] = useState(null);
  const [saving, setSaving] = useState(false);
  const ed = usePlayDraft(cfg);
  const { stop } = ed;
  const { primed, replay } = useReplay(setHit);

  useEffect(() => { setHit(null); stop(); }, [cfg, stop]);
  useEffect(() => {
    if (!jump) return;
    setSit({ outs: jump.outs, runners: jump.runners, hitType: jump.hitType });
    setHit({ id: Date.now(), tap: jump.tap, foul: false });
    stop();
  }, [jump, stop]);
  useEffect(() => { loadPlays(sit).then(setPlays).catch(() => setPlays([])); }, [sit, loadPlays]);

  const current = useMemo(() => {
    if (!hit || hit.foul) return null;
    const c = classifyHit(hit.tap, cfg, sit.hitType);
    return { ...c, play: plays.find((p) => p.fielder === c.fielder && p.hit_type === sit.hitType) };
  }, [cfg, hit, plays, sit.hitType]);

  const play = current?.play;
  const resolved = useMemo(() => (play ? resolvePlayers(play, current.ball, cfg) : readyPositions(cfg)), [play, current, cfg]);
  const positions = ed.draft ? ed.draft.players : primed ? readyPositions(cfg) : resolved;
  const shownHit = hit && { ...hit, ball: current?.ball ?? hit.tap, hitType: sit.hitType };
  const update = (patch) => { stop(); setSit((s) => ({ ...s, ...patch })); };
  const onTap = (tap) => setHit({ id: Date.now(), tap, foul: classifyHit(tap, cfg, sit.hitType).foul });
  const replacePlay = (p) => setPlays((list) => list.map((q) => (q.key === p.key ? p : q)));

  const run = async (fn, msg) => {
    setSaving(true);
    try {
      replacePlay(await fn());
      stop();
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const save = () => run(() => onSave(ed.payload(play, current.ball)), "Saved as your team's version");
  const reset = () => run(() => onReset(play.key), "Play reset to default");

  return (
    <div className="grid gap-3 lg:gap-x-6 lg:gap-y-4 lg:grid-cols-[1.4fr_1fr] items-start">
      <div className="card !p-3 lg:!p-4 lg:col-start-2 lg:row-start-1">
        <SituationControls outs={sit.outs} runners={sit.runners} hitType={sit.hitType} onChange={update} />
      </div>
      <div className="space-y-3 lg:col-start-1 lg:row-start-1 lg:row-span-2">
        <div className={`field-frame ${ed.draft ? "field-frame-edit" : ""}`}>
          <PlayField
            cfg={cfg} runners={sit.runners} positions={positions}
            throws={ed.draft ? ed.draft.throws : play?.throws ?? []}
            runnerPaths={ed.draft ? ed.draft.runner_paths : play?.runner_paths ?? []}
            hit={primed ? null : shownHit} instant={primed} playId={`${play?.key}-${hit?.id}`} onTap={onTap}
            edit={ed.draft && { selected: ed.selected, onSelect: ed.setSelected, onDrag: ed.drag }}
          />
          {play && <div className="field-badge hidden sm:block"><PlayBadge play={play} /></div>}
          <div className="absolute top-2.5 right-2.5 flex gap-2">
            {editable && play && !ed.draft && (
              <button data-testid="edit-play-button" className="reset-btn !static !bg-[var(--accent-strong)]" onClick={() => ed.start(play, resolved)}>
                <Pencil size={18} /> Edit
              </button>
            )}
            {play && !ed.draft && (
              <button data-testid="replay-button" className="reset-btn !static" onClick={replay} aria-label="Replay play">
                <PlayIcon size={18} /> <span className="hidden sm:inline">Replay</span>
              </button>
            )}
            {onExportPdf && play && !ed.draft && (
              <button data-testid="export-pdf-button" className="reset-btn !static" onClick={() => onExportPdf(play, current.ball)} aria-label="Export PDF">
                <FileDown size={18} /> <span className="hidden sm:inline">PDF</span>
              </button>
            )}
            {onAssign && play && !ed.draft && (
              <button data-testid="assign-practice-button" className="reset-btn !static" onClick={() => onAssign(play, current.ball)} aria-label="Assign to practice">
                <Target size={18} /> <span className="hidden sm:inline">Assign</span>
              </button>
            )}
            {hit && !ed.draft && (
              <button data-testid="reset-field-button" className="reset-btn !static" onClick={() => setHit(null)}>
                <RotateCcw size={18} /> Reset
              </button>
            )}
          </div>
        </div>
        <Legend />
      </div>
      <div className="lg:col-start-2 lg:row-start-2 space-y-3">
        {ed.draft && play && (
          <EditPanel
            play={play} draft={ed.draft} runners={sit.runners} selected={ed.selected} saving={saving}
            onPlayer={(pos, patch) => ed.patchPlayer(pos, patch, current.ball)} onDraft={ed.patchDraft}
            onSave={save} onCancel={stop} onReset={reset}
          />
        )}
        {!ed.draft && <ExplanationCard hit={shownHit} play={play} positions={positions} />}
      </div>
    </div>
  );
};
